import {
  AltType,
  NotificationChannel,
  NotificationConfig,
  ReceiverType,
} from '@/types/notification'
import { Ref, reactive, ref } from 'vue'

import Calendar, { GuestCollectionMode } from '@/models/calendar'
import Email from '@/models/email'
import { formatHTMLForTipTap } from '@gohighlevel/ghl-text-editor'
import { Editor } from '@tiptap/vue-3'
import { getDefaultEmailSubjectAndBody, getDefaultWhatsAppTemplateLabel } from '../utils/default-notification-template'
import AppState, { getWhatsAppTemplates } from './app'

const receiverTypes: string[] = Object.values(ReceiverType)

export const getReceiverTypesForChannel = (channel: string): string[] => {
  const channelStr = channel?.toString()

  if (channel === NotificationChannel.SMS) {
    // Ensure correct order: contact, assigned user, guest, additional phone numbers
    return [
      ReceiverType.CONTACT,
      ReceiverType.ASSIGNED_USER,
      ReceiverType.GUEST,
      ReceiverType.PHONE_NUMBERS,
    ]
  } else if (channel === NotificationChannel.EMAIL) {
    // Ensure correct order: contact, assigned user, guest, additional emails
    return [ReceiverType.CONTACT, ReceiverType.ASSIGNED_USER, ReceiverType.GUEST, ReceiverType.EMAILS]
  } else if (channelStr === 'whatsapp') {
    // For WhatsApp, only support contact, assigned user, and additional phone numbers
    return [ReceiverType.CONTACT, ReceiverType.ASSIGNED_USER, ReceiverType.PHONE_NUMBERS]
  } else {
  // For in-app notifications: contact, assigned user, guest
  return [ReceiverType.CONTACT, ReceiverType.ASSIGNED_USER, ReceiverType.GUEST]
}
}

export const guestReceiverVisibleForChannel = (
  channel: NotificationChannel | string,
  enableGuests: boolean,
  guestCollectionMode?: GuestCollectionMode | string
): boolean => {
  if (!enableGuests) {
    return false
  }
  const mode = guestCollectionMode || GuestCollectionMode.EMAIL
  if (channel === NotificationChannel.EMAIL) {
    return mode !== GuestCollectionMode.PHONE
  }
  if (channel === NotificationChannel.SMS) {
    return mode !== GuestCollectionMode.EMAIL
  }
  return true
}

/**
 * Sort notifications array by receiver type according to the desired order for the channel
 * This ensures consistent ordering regardless of backend response order
 */
export const sortNotificationsByReceiverType = (
  notifications: NotificationConfig[],
  channel: string
): NotificationConfig[] => {
  const desiredOrder = getReceiverTypesForChannel(channel)

  return notifications.sort((a, b) => {
    const indexA = desiredOrder.indexOf(a.receiverType)
    const indexB = desiredOrder.indexOf(b.receiverType)

    // If receiver type not found in desired order, put it at the end
    const orderA = indexA === -1 ? desiredOrder.length : indexA
    const orderB = indexB === -1 ? desiredOrder.length : indexB

    return orderA - orderB
  })
}

export const eventNotificationState = reactive({
  calendarId: '',
  isEventCalendar: false,
  isCustomFormSelected: false,
  calendar: {} as Calendar | null,
  isLoading: false,
  saving: false,
  disableSave: false,
  /** Incremented per configurator while merge-field syntax is invalid; disables Save in notification modal */
  templateVariableErrorCount: 0,
  showEmailTemplateRefresh: false,
  eventNotificationData: [],
  eventNotifications: [] as NotificationConfig[],
  smsNotifications: [] as NotificationConfig[],
  whatsappNotifications: [] as NotificationConfig[],
  accordions: [] as {
    id: string
    isOpen: boolean
    isChecked: boolean
  }[],
  lastOpenedAccordion: null as number | null,
  inAppNotification: {} as NotificationConfig,
  enableNotification: false,
  contact: {},
  guest: {},
  assignedUser: {},
  additionalEmails: {},
})
export const resetState = () => {
  eventNotificationState.eventNotifications = []
  eventNotificationState.smsNotifications = []
  eventNotificationState.whatsappNotifications = []
  eventNotificationState.contact = {}
  eventNotificationState.guest = {}
  eventNotificationState.assignedUser = {}
  eventNotificationState.additionalEmails = {}
  eventNotificationState.templateVariableErrorCount = 0
}
export class EventNotification {
  actionTemplate: Ref<Email | undefined>
  state = reactive({} as NotificationConfig)
  editor: Editor
  toAddress: Ref<string | undefined>
  testPhoneNumber: Ref<string | undefined>
  open = ref(false)
  checked = ref(false)
  constructor(props: NotificationConfig) {
    this.actionTemplate = ref<Email>(new Email())
    this.editor = [] as any
    this.toAddress = ref<string | undefined>(undefined)
    this.testPhoneNumber = ref<string | undefined>(undefined)
    this.open = ref(false)
    this.checked = ref(false)
    this.state = reactive({
      _id: props._id,
      altType: props.altType,
      altId: props.altId,
      receiverType: props.receiverType,
      additionalEmailIds: props.additionalEmailIds,
      additionalPhoneNumbers: props.additionalPhoneNumbers,
      channel: props.channel,
      notificationType: props.notificationType,
      isActive: props.isActive,
      templateId: props.templateId,
      body: formatHTMLForTipTap(props.body || ''),
      subject: props.subject,
      beforeTime: props.beforeTime || [],
      afterTime: props.afterTime || [],
      selectedUsers: props.selectedUsers,
      fromAddress: props.fromAddress || '',
      fromName: props.fromName || '',
      deleted: props.deleted ?? false,
    })
  }
}

export const updateStateOfNotification = (
  id: string,
  state: Partial<NotificationConfig>
) => {
  const index = eventNotificationState.eventNotifications.findIndex(
    notification => notification._id === id
  )
  if (index !== -1) {
    eventNotificationState.eventNotifications[index] = {
      ...eventNotificationState.eventNotifications[index],
      ...state,
    }
  } else {
    console.warn(`Notification with id ${id} not found.`)
  }
}

export const getDefaultNotifications = (
  notification: Partial<NotificationConfig>
) => {
  const defaultNotifications = [] as NotificationConfig[]
  const channel = notification.channel || NotificationChannel.EMAIL
  const channelReceiverTypes = getReceiverTypesForChannel(channel as string)

  channelReceiverTypes.forEach(receiverType => {
    if (
      !(
        eventNotificationState.isEventCalendar &&
        receiverType === ReceiverType.ASSIGNED_USER
      )
    ) {
      notification.receiverType = receiverType as any
      notification.channel = channel
      const defaultNotification = getDefaultNotification(notification)
      if (defaultNotification) {
        defaultNotifications.push(defaultNotification as NotificationConfig)
      }
    }
  })
  return defaultNotifications
}

// WhatsApp-specific async version
export const getDefaultWhatsAppNotifications = async (
  notification: Partial<NotificationConfig>
) => {
  const defaultNotifications = [] as NotificationConfig[]
  const channel = notification.channel || NotificationChannel.WHATSAPP
  const channelReceiverTypes = getReceiverTypesForChannel(channel as string)

  for (const receiverType of channelReceiverTypes) {
    if (
      !(
        eventNotificationState.isEventCalendar &&
        receiverType === ReceiverType.ASSIGNED_USER
      )
    ) {
      notification.receiverType = receiverType as any
      notification.channel = channel
      const defaultNotification = await getDefaultWhatsAppNotification(notification)
      if (defaultNotification) {
        defaultNotifications.push(defaultNotification as NotificationConfig)
      }
    }
  }
  return defaultNotifications
}

export const getDefaultNotification = (
  notification: Partial<NotificationConfig>
): NotificationConfig | null => {
  const defaultTemplate = getDefaultEmailSubjectAndBody(
    notification,
    eventNotificationState.isCustomFormSelected
  )
  // Guest rows are only bootstrapped when the calendar collects guests —
  // mirrored for email and SMS (no guest default row when guests are disabled)
  if (
    (notification.channel === NotificationChannel.EMAIL ||
      notification.channel === NotificationChannel.SMS) &&
    notification.receiverType === ReceiverType.GUEST &&
    !AppState.editCalendarValidationData.enableGuests
  ) {
    return null
  }
  if (
    notification.channel === NotificationChannel.EMAIL &&
    eventNotificationState.isEventCalendar &&
    notification.receiverType === ReceiverType.ASSIGNED_USER
  ) {
    return null
  }
  // For non-WhatsApp channels, templateId remains empty
  let defaultTemplateId = ''

  const defaultNotification = {
    _id: notification._id || '',
    altType: AltType.CALENDAR,
    altId: notification.altId || '',
    receiverType: notification.receiverType,
    channel: notification.channel || NotificationChannel.EMAIL,
    notificationType: notification.notificationType,
    isActive: true,
    templateId: defaultTemplateId,
    body: defaultTemplate.html || defaultTemplate.smsText || '',
    subject: defaultTemplate.subject,
    beforeTime: [],
    afterTime: [],
    selectedUsers: [],
    deleted: false,
  } as NotificationConfig

  // Add channel-specific fields
  const channelStr = notification.channel?.toString()
  if (channelStr === 'sms' || channelStr === 'whatsapp') {
    defaultNotification.additionalPhoneNumbers = []
  } else {
    defaultNotification.additionalEmailIds = []
  }

  return defaultNotification
}

// WhatsApp-specific async version that resolves template IDs
export const getDefaultWhatsAppNotification = async (
  notification: Partial<NotificationConfig>
): Promise<NotificationConfig | null> => {
  const defaultTemplate = getDefaultEmailSubjectAndBody(
    notification,
    eventNotificationState.isCustomFormSelected
  )
  if (
    notification.channel === NotificationChannel.EMAIL &&
    ((notification.receiverType === ReceiverType.GUEST &&
      !AppState.editCalendarValidationData.enableGuests) ||
      (eventNotificationState.isEventCalendar &&
        notification.receiverType === ReceiverType.ASSIGNED_USER))
  ) {
    return null
  }

  // Get default template ID for WhatsApp notifications
  let defaultTemplateId = ''
  if (notification.channel?.toString() === 'whatsapp') {
    const defaultTemplateLabel = getDefaultWhatsAppTemplateLabel(
      notification.notificationType || '',
      notification.receiverType || ''
    )

    if (defaultTemplateLabel) {
      try {
        // Fetch WhatsApp templates to get the actual template ID
        const whatsappTemplates = await getWhatsAppTemplates()
        const defaultTemplate = whatsappTemplates.find(template =>
            template.label &&
            template.label.toLowerCase() === defaultTemplateLabel.toLowerCase()
        )

        if (defaultTemplate) {
          defaultTemplateId = defaultTemplate.value as string
        }
      } catch (error) {
        console.warn('Error fetching WhatsApp templates for default notification:', error)
      }
    }
  }

  const defaultNotification = {
    _id: notification._id || '',
    altType: AltType.CALENDAR,
    altId: notification.altId || '',
    receiverType: notification.receiverType,
    channel: notification.channel || NotificationChannel.EMAIL,
    notificationType: notification.notificationType,
    isActive: true,
    templateId: defaultTemplateId,
    body: defaultTemplate.html || defaultTemplate.smsText || '',
    subject: defaultTemplate.subject,
    beforeTime: [],
    afterTime: [],
    selectedUsers: [],
    deleted: false,
  } as NotificationConfig

  // Add channel-specific fields
  const channelStr = notification.channel?.toString()
  if (channelStr === 'sms' || channelStr === 'whatsapp') {
    defaultNotification.additionalPhoneNumbers = []
  } else {
    defaultNotification.additionalEmailIds = []
  }

  return defaultNotification
}

export function normalizeNotificationApiResponse(
  data: NotificationConfig | NotificationConfig[] | undefined | null
): NotificationConfig[] {
  if (!data) {
    return []
  }
  return Array.isArray(data) ? data : [data]
}

type ReceiverConfiguratorBucket = {
  state: NotificationConfig
}

function getReceiverConfiguratorBucket(
  receiverType: string
): ReceiverConfiguratorBucket | undefined {
  const bucket = eventNotificationState[receiverType as keyof typeof eventNotificationState]
  if (bucket && typeof bucket === 'object' && 'state' in bucket) {
    return bucket as ReceiverConfiguratorBucket
  }
  return undefined
}

function normalizeNotificationChannel(
  channel?: string | NotificationChannel
): string | undefined {
  return channel?.toString().toLowerCase()
}

function inferNotificationChannel(receiverType: string): string | undefined {
  if (
    eventNotificationState.smsNotifications.some(
      notification => notification.receiverType === receiverType
    )
  ) {
    return NotificationChannel.SMS
  }
  if (
    eventNotificationState.whatsappNotifications.some(
      notification => notification.receiverType === receiverType
    )
  ) {
    return NotificationChannel.WHATSAPP
  }
  if (
    eventNotificationState.eventNotifications.some(
      notification => notification.receiverType === receiverType
    )
  ) {
    return NotificationChannel.EMAIL
  }
  return undefined
}

/** Resolve persisted _id by receiverType — never by response or UI array index. */
export function resolvePersistedNotificationId(
  receiverType: string,
  configRows: NotificationConfig[],
  receiverState?: NotificationConfig
): string | undefined {
  const configId = configRows.find(row => row.receiverType === receiverType)?._id
  const stateId = receiverState?._id

  if (!isVirtualNotification(configId)) {
    return configId
  }
  if (!isVirtualNotification(stateId)) {
    return stateId
  }
  return configId || stateId || undefined
}

/** Merge editor state with config-array rows; config array is source of truth for persisted _id. */
export function buildSavePayloadFromNotifications(
  notifications: NotificationConfig[]
): NotificationConfig[] {
  return notifications.map(notification => {
    const receiverState = getReceiverConfiguratorBucket(notification.receiverType)?.state
    const persistedId = resolvePersistedNotificationId(
      notification.receiverType,
      notifications,
      receiverState
    )
    if (!receiverState) {
      return persistedId ? { ...notification, _id: persistedId } : notification
    }
    return {
      ...receiverState,
      _id: persistedId ?? '',
      receiverType: notification.receiverType,
      channel: notification.channel ?? receiverState.channel,
    }
  })
}

function syncReceiverConfiguratorState(
  notification: NotificationConfig,
  overrides?: Partial<NotificationConfig>
) {
  const receiverBucket = getReceiverConfiguratorBucket(notification.receiverType)
  if (!receiverBucket) {
    return
  }
  const existingState = receiverBucket.state
  const preservedBeforeTime =
    notification.beforeTime?.length === 0 && existingState.beforeTime?.length > 0
      ? existingState.beforeTime
      : notification.beforeTime
  const preservedAfterTime =
    notification.afterTime?.length === 0 && existingState.afterTime?.length > 0
      ? existingState.afterTime
      : notification.afterTime

  Object.assign(receiverBucket.state, notification, overrides, {
    _id: notification._id,
    receiverType: notification.receiverType,
    beforeTime: preservedBeforeTime,
    afterTime: preservedAfterTime,
  })
}

export function stripNotificationIdForCreate<T extends { _id?: string }>(
  notification: T
): Omit<T, '_id'> {
  const { _id: _ignored, ...rest } = notification
  return rest
}

export const isVirtualNotification = (id: string | undefined | null): boolean => {
  return !id
}

export const isInAppNotificationChannel = (channel?: string | NotificationChannel): boolean => {
  return channel?.toString() === NotificationChannel.IN_APP
}

export function applyInAppCreateResult(
  data: NotificationConfig | NotificationConfig[] | undefined | null,
  deleted?: boolean
) {
  const created = normalizeNotificationApiResponse(data)[0]
  if (!created?._id) {
    return
  }
  eventNotificationState.inAppNotification._id = created._id
  if (deleted !== undefined) {
    eventNotificationState.inAppNotification.deleted = deleted
  }
}

function upsertChannelNotificationRow(
  targetConfig: NotificationConfig[],
  notification: NotificationConfig
) {
  const index = targetConfig.findIndex(n => n.receiverType === notification.receiverType)
  if (index !== -1) {
    targetConfig[index] = {
      ...targetConfig[index],
      ...notification,
      _id: notification._id ?? targetConfig[index]._id,
      receiverType: notification.receiverType,
    }
  } else {
    targetConfig.push(notification)
  }
}

export function updateNotificationState(
  data: NotificationConfig | NotificationConfig[],
  deleted?: boolean
) {
  normalizeNotificationApiResponse(data).forEach(originalNotification => {
    const notification = {
      ...originalNotification,
      deleted: deleted !== undefined ? deleted : originalNotification.deleted,
    }

    notification.body = formatHTMLForTipTap(notification.body)

    if (!notification.channel) {
      const inferredChannel = inferNotificationChannel(notification.receiverType)
      if (inferredChannel) {
        notification.channel = inferredChannel
      }
    }

    if (isInAppNotificationChannel(notification.channel)) {
      return
    }

    const channel = normalizeNotificationChannel(notification.channel)

    if (channel === NotificationChannel.SMS) {
      upsertChannelNotificationRow(eventNotificationState.smsNotifications, notification)
    } else if (channel === NotificationChannel.WHATSAPP) {
      upsertChannelNotificationRow(
        eventNotificationState.whatsappNotifications,
        notification
      )
    } else {
      upsertChannelNotificationRow(eventNotificationState.eventNotifications, notification)
    }

    syncReceiverConfiguratorState(notification)
  })

  // Sort all notification arrays to maintain consistent ordering after updates
  eventNotificationState.eventNotifications = sortNotificationsByReceiverType(
    eventNotificationState.eventNotifications,
    NotificationChannel.EMAIL
  )
  eventNotificationState.smsNotifications = sortNotificationsByReceiverType(
    eventNotificationState.smsNotifications,
    NotificationChannel.SMS
  )
  eventNotificationState.whatsappNotifications = sortNotificationsByReceiverType(
      eventNotificationState.whatsappNotifications,
      'whatsapp'
    )
}

export function getEventNotificationState(tab: string) {
  // check if all the email notification are enabled
  // all are disabled
  let isNotificationsEnabled = true
  if (tab === 'inApp') {
    const isAvailableInDb = !!eventNotificationState.inAppNotification._id
    if (!isAvailableInDb) {
      return !!isAvailableInDb
    }
    return !eventNotificationState.inAppNotification.deleted
  }
  if (tab === 'email') {
    const isAvailableInDb = eventNotificationState.eventNotifications.find(
      notification => notification._id
    )
    if (!isAvailableInDb) {
      return !!isAvailableInDb
    }
    if (
      eventNotificationState.eventNotifications.every(
        notification => notification.deleted
      )
    ) {
      isNotificationsEnabled = false
    }
    if (
      eventNotificationState.inAppNotification.notificationType === 'booked'
    ) {
      const isStoredData = eventNotificationState.eventNotifications.find(
        notification => notification._id
      )
      if (!isStoredData) {
        return !eventNotificationState.eventNotifications
      }
    }
  }
  if (tab === 'sms') {
    const isAvailableInDb = eventNotificationState.smsNotifications.find(
      notification => notification._id
    )
    if (!isAvailableInDb) {
      return !!isAvailableInDb
    }
    if (
      eventNotificationState.smsNotifications.every(
        notification => notification.deleted
      )
    ) {
      isNotificationsEnabled = false
    }
    if (
      eventNotificationState.inAppNotification.notificationType === 'booked'
    ) {
      const isStoredData = eventNotificationState.smsNotifications.find(
        notification => notification._id
      )
      if (!isStoredData) {
        return !eventNotificationState.smsNotifications
      }
    }
  }
  if (tab === 'whatsapp') {
    const isAvailableInDb = eventNotificationState.whatsappNotifications.find(
      notification => notification._id
    )
    if (!isAvailableInDb) {
      return !!isAvailableInDb
    }
    if (
      eventNotificationState.whatsappNotifications.every(
        notification => notification.deleted
      )
    ) {
      isNotificationsEnabled = false
    }
    if (
      eventNotificationState.inAppNotification.notificationType === 'booked'
    ) {
      const isStoredData = eventNotificationState.whatsappNotifications.find(
        notification => notification._id
      )
      if (!isStoredData) {
        return !eventNotificationState.whatsappNotifications
      }
    }
  }
  return isNotificationsEnabled
}
